import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import EchartDoughnutChart from "../../../../Components/GraphNCharts/EchartDoughnut";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import {
  fixedTons,
  formatDashboardEmissionValue,
  formatNumberScientificCompact,
  formatTruncatedDecimalPlaces,
} from "../../../../utils/common";
import useAuthStore from "../../../../store/ContextUser";
import { get } from "../../../../utils/api";

const Dashboard = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { selectedCompany } = useAuthStore();

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [dashboardData, setDashboardData] = useState({
    emission: {
      totalLeasedAreaUnits: 0,
      totalEmissions: 0,
      totalElectricityConsumption: 0,
      emissionIntensity: 0,
    },
    emissionByAssetType: { labels: [], dataPoints: [] },
    topAssets: { labels: [], dataPoints: [] },
  });

  const apiurls = useMemo(
    () => ({
      emission: `/dashboard/scope3/streamleased/totalUpstreamLeasedEmission?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
      emissionByCatoryAndCountry: `/dashboard/scope3/streamleased/upstreamLeasedPageData?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
    }),
    [fromDate, toDate, selectedFacility, selectedCompany],
  );

  const normalizeEmission = (payload) => {
    const data = payload?.data || payload || {};
    return {
      totalLeasedAreaUnits: Number(data?.totalLeasedAreaUnits || 0),
      totalEmissions: Number(data?.totalEmissions || 0),
      totalElectricityConsumption: Number(
        data?.totalElectricityConsumption || 0,
      ),
      emissionIntensity:
        data?.emissionIntensity == null
          ? 0
          : Number(data?.emissionIntensity || 0),
      totalLeaseAreaUnitsByUnit: Array.isArray(data?.totalLeaseAreaUnitsByUnit)
        ? data.totalLeaseAreaUnitsByUnit
        : [],
    };
  };

  const formatCardMetric = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    if (n === 0) return "0";
    const abs = Math.abs(n);
    if (abs > 0 && abs < 0.1) {
      return formatNumberScientificCompact(fixedTons(n, 2));
    }
    return formatTruncatedDecimalPlaces(fixedTons(n, 3));
  };

  const fetchData = async () => {
    try {
      const [emissionRes, emissionData] = await Promise.all([
        get(apiurls.emission),
        get(apiurls.emissionByCatoryAndCountry),
      ]);

      const emission = normalizeEmission(emissionRes?.data);

      const assetTypeData = emissionData?.data?.emissionByAssetType || [];
      const topAssetsData =
        emissionData?.data?.top5EmissionContributingAssets || [];

      const emissionByAssetType = assetTypeData.filter(
        (item) => item?.name && Number(item?.totalEmission) > 0,
      );
      const topAssets = topAssetsData.filter(
        (item) => item?.name && Number(item?.totalEmission) >= 0,
      );

      setDashboardData({
        emission,
        emissionByAssetType: {
          labels: emissionByAssetType.map((item) => item.name),
          dataPoints: emissionByAssetType.map((item) =>
            Number(item.totalEmission || 0),
          ),
        },
        topAssets: {
          labels: topAssets.map((item) => item.name),
          dataPoints: topAssets.map((item) => Number(item.totalEmission || 0)),
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (!selectedCompany?._id) return;
    fetchData();
  }, [fromDate, toDate, selectedFacility, selectedCompany?._id]);

  const generateYearList = (startYear = 2020) => {
    const years = [];
    for (let year = startYear; year <= currentYear; year++) years.push(year);
    return years;
  };

  const yearList = generateYearList(2020);
  const assetTypeColors = [
    "#0052CC",
    "#7B2CBF",
    "#FF3D3D",
    "#37C837",
    "#FFB000",
    "#008000",
    "#0000FF",
    "#800080",
    "#FF00FF",
    "#00FFFF",
    "#FFA500",
    "#800000",
    "#008080",
  ];
  const hasEmissionByAssetType =
    (dashboardData?.emissionByAssetType?.labels || []).length > 0 &&
    (dashboardData?.emissionByAssetType?.dataPoints || []).length > 0;
  const leasedAreaByUnit = (dashboardData?.emission?.totalLeaseAreaUnitsByUnit || []).reduce(
    (acc, item) => {
      const value = Number(item?.totalLeasedAreaUnits || 0);
      const unit = String(item?.assetSizeUnit || "").toLowerCase();
      if (unit.includes("unit")) {
        acc.units += value;
      } else {
        acc.area += value;
      }
      return acc;
    },
    { area: 0, units: 0 },
  );

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Upstream Leased Assets</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Emission Analysis</p>

        <div className={styles.emission_container}>
          <div className={styles.card_container}>
            <p className={styles.graph_heading}>Total Fuel Emissions (tCO₂e)</p>
            <p className={styles.card_value} style={{ marginTop: "2rem" }}>
              {formatCardMetric(dashboardData.emission.totalEmissions)}
            </p>
          </div>

          <div className={styles.card_container}>
            <p className={styles.graph_heading}>
              Emission Intensity (tCO₂e /sq. ft.)
            </p>
            <p className={styles.card_value} style={{ marginTop: "2rem" }}>
              {formatCardMetric(dashboardData.emission.emissionIntensity)}
            </p>
          </div>
        </div>

        <div className={styles.doughnutChartRow}>
          <div
            className={`${styles.chart_container} ${styles.doughnutHalfCard}`}
          >
            <p className={styles.graph_heading}>Emission by asset type (tCO₂e)</p>
            <div className={styles.doughnutContent}>
              <div
                className={
                  hasEmissionByAssetType
                    ? styles.doughnutChartSlot
                    : styles.doughnutChartSlotFull
                }
              >
                <EchartDoughnutChart
                  labels={dashboardData?.emissionByAssetType?.labels || []}
                  dataPoints={
                    dashboardData?.emissionByAssetType?.dataPoints || []
                  }
                  colors={assetTypeColors}
                  customHeight="220px"
                  formatDisplayValue={formatDashboardEmissionValue}
                  fixTooltipClipping
                />
              </div>
              {hasEmissionByAssetType && (
                <div className={styles.doughnutLegend}>
                  {(dashboardData?.emissionByAssetType?.labels || []).map(
                    (label, idx) => (
                      <div
                        key={label}
                        className={styles.doughnutLegendItem}
                      >
                        <div className={styles.doughnutLegendLabel}>
                          <span
                            className={styles.doughnutLegendMarker}
                            style={{ "--legend-color": assetTypeColors[idx] }}
                          />
                          <span className={styles.doughnutLegendText}>
                            {label}
                          </span>
                        </div>
                        <span className={styles.doughnutLegendValue}>
                          {formatDashboardEmissionValue(
                            dashboardData?.emissionByAssetType?.dataPoints?.[
                            idx
                            ] ?? 0,
                          )}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            className={styles.emission_container}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.2rem",
              width: "calc(50% - 0.6rem)",
            }}
          >
            <div
              className={styles.card_container}
              style={{ width: "100%", height: "calc(50% - 0.6rem)" }}
            >
              <p className={styles.graph_heading}>
                Total Electricity Emissions (tCO₂e)
              </p>
              <p className={styles.card_value} style={{ marginTop: "2rem" }}>
                {formatCardMetric(
                  dashboardData.emission.totalElectricityConsumption,
                )}{" "}

              </p>
            </div>

            <div
              className={styles.card_container}
              style={{ width: "100%", height: "calc(50% - 0.6rem)" }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <p className={styles.graph_heading}>Total Leased Area (sq. ft.)</p>
                  <p className={styles.card_value}>{formatCardMetric(leasedAreaByUnit.area)}</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <p className={styles.graph_heading}>Total Leased Units (Unit)</p>
                  <p className={styles.card_value}>{formatCardMetric(leasedAreaByUnit.units)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={styles.chart_container}
          style={{ width: "calc(100% - 0.6rem)" }}
        >
          <div className={styles.graph_container_header}>
            <p
              className={`${styles.graph_heading} ${styles.graph_heading_bar_ref}`}
            >
              Top 5 Emission-Contributing Assets (tCO₂e)
            </p>
          </div>
          <BarChart
            labels={dashboardData?.topAssets?.labels || []}
            datasets={[
              {
                label: "Emissions",
                data: dashboardData?.topAssets?.dataPoints || [],
                backgroundColor: "rgba(59, 130, 246, 0.95)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 0,
              },
            ]}
            customHeight="260px"
            ellipsis={false}
            linearTickFormatter={formatDashboardEmissionValue}
            horizontalCleanStyle
            valueAxisTitle="Emissions (tCO₂e)"
          />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
